import React, { useContext, useEffect, useState } from 'react';
import { MerchantType } from '../../../interfaces/delivery-company/merchants';
import { API_ENDPOINT } from '../../../configurations/global.config';
import { DoubleDate } from '../../common/double-date-picker/double-date-picker.interfaces';
import { useImmer } from 'use-immer';
import { AuthContext } from '../../../contexts/user-context/user-context';
import axios, { AxiosResponse } from 'axios';
import {
  Box,
  Card,
  Accordion,
  AccordionSummary,
  Typography,
  AccordionDetails,
  Chip,
  TablePagination,
  Alert,
} from '@mui/material';
import DoubleDatePicker from '../../common/double-date-picker/double-date-picker';
import FilterBar from '../../common/filter-bar/filter-bar';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { buildQueryParams } from '../../common/filter-bar/filter-bar.utils';
import { PaginationContext } from '../../../contexts/pagination-context';

import {
  ActorFilterOption,
  DeliveryStateFilterOption,
  IFilterOption,
} from '../../common/filter-bar/types';
import PickupTaskDialog from '../pickup-tasks/pickup-task-dialog';
import { SortingByColumnState } from '../../common/orders-listing-table/utils';
import ResponsivePickupTasksListing from '../common/pickup-tasks-listing';
import { IPickupTask } from '../../../interfaces/delivery-company/tasks';
import TaskWaybillsDialog from '../pickup-tasks/tasks-listing/task-waybills-dialog';
import ReceiveOrdersDialog from '../pickup-tasks/receive-orders-dialog/receive-orders-dialog';
import ConfirmAction from '../../common/confirm-action/confirm-action';
import { useSnackBar } from '../../../contexts/snackbar';
import { PickupTaskState } from '../../../enums/tasks';
import { PickupTaskFilterKeys } from '../../common/filter-bar/keys-config';

export interface MerchantPickupsProps {
  merchant: MerchantType;
}

function MerchantPickups({ merchant }: MerchantPickupsProps) {
  const { showMessage } = useSnackBar();
  const [response, setResponse] = useState<AxiosResponse>();
  const [pickupList, setPickupsList] = useState<IPickupTask[]>([]);
  const [pickupsCount, setPickupsCount] = useState(0);
  const [dateFilter, setDateFilter] = useState<DoubleDate>({
    startDate: null,
    endDate: null,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [editingTask, setEditingTask] = useState<IPickupTask>();
  const [receiveOrdersForTask, setReceiveOrdersForTask] =
    useState<IPickupTask>();
  const [cancellingPickupRequest, setCancellingPickupRequest] =
    useState<IPickupTask>();
  const [downloadWaybillForTask, setDownloadWaybillForTask] =
    useState<IPickupTask>();
  const { user, config } = useContext(AuthContext);

  const relevant_fields = [
    'id',
    'status',
    'package_count',
    'workflow',
    'creation_date',
    'created_by',
    'customer',
    'driver',
    'merchant',
    'merchant_location',
    'customer_location',
    'is_critical',
  ];

  const { paginationConfig, setPaginationConfig } =
    useContext(PaginationContext);

  const handleCancelTask = async () => {
    try {
      setResponse(undefined);
      setIsLoading(true);
      const endpoint = `${API_ENDPOINT}/api/v1/pickup-task/${cancellingPickupRequest?.id}/`;
      const response = await axios.delete(endpoint, config);
      setResponse(response);
      setPickupsList(
        pickupList.map((task) =>
          task.id === cancellingPickupRequest?.id
            ? { ...task, status: PickupTaskState.CANCELLED }
            : task
        )
      );
    } catch (e) {
      if (axios.isAxiosError(e)) {
        setResponse(e.response);
        showMessage('Failed to cancel pickup request: ', 'error');
      } else {
        showMessage('Failed to cancel pickup request', 'error');
      }
    }
    setIsLoading(false);
  };

  const [controller, setController] = useState({
    page: 0,
    rowsPerPage: paginationConfig.rowsPerPage,
  });

  const [filters, setFilters] = useState<IFilterOption[]>([
    new ActorFilterOption(
      {
        actortype: 'merchant',
        val: merchant,
      },
      true
    ),
  ]);

  const [sorting, setSorting] = useState<SortingByColumnState>({
    column: 'creation_date',
    order: 'decreasing',
  });

  useEffect(() => {
    let response: any;
    const getData = async () => {
      let endpoint = `${API_ENDPOINT}/api/v1/pickup-tasks/?page=${controller.page + 1}&page_size=${controller.rowsPerPage}&fields=${relevant_fields.join(',')}`;

      const filterQueryParams = buildQueryParams(filters);
      endpoint += `${filterQueryParams}`;

      const { startDate, endDate } = dateFilter;

      if (startDate !== null) {
        endpoint += `&date_from=${encodeURIComponent(startDate.format('YYYY-MM-DD'))}`;
      }

      if (endDate !== null) {
        endpoint += `&date_to=${encodeURIComponent(endDate.format('YYYY-MM-DD'))}`;
      }

      endpoint += `&sort_by_${sorting.order}=${encodeURIComponent(sorting.column)}`;

      console.log(endpoint);
      try {
        response = await axios.get<{ count: number; results: IPickupTask[] }>(
          endpoint,
          config
        );

        setPickupsList(response.data.results);
        setPickupsCount(response.data.count);
      } catch (error) {
        console.log(error);
      }
      setIsLoading(false);
    };
    setIsLoading(true);
    getData();
    console.log(response);
  }, [controller, dateFilter, filters, sorting]);

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ) => {
    setController({
      ...controller,
      page: newPage,
    });
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setPaginationConfig({ ...paginationConfig, rowsPerPage: 10 });
    setController({
      ...controller,
      rowsPerPage: newRowsPerPage,
      page: 0,
    });
  };

  const searchQueryAction = (
    column: string,
    value: string,
    operation: string
  ) => {
    setController({
      ...controller,
      page: 0,
    });
  };

  const resetPaginationControls = () => {
    setController({
      ...controller,
      page: 0,
      rowsPerPage: paginationConfig.rowsPerPage,
    });
  };
  const filterQueryAction = (filters: IFilterOption[]) => {
    resetPaginationControls();
    setFilters(filters);
  };

  const dateFilterAction = (dateFilterValue: DoubleDate) => {
    resetPaginationControls();
    setDateFilter(dateFilterValue);
  };

  const resetAll = () => {
    resetPaginationControls();
  };

  return (
    <Box>
      <Accordion elevation={0}>
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <Typography
            variant="h5"
            component="h5"
            sx={{ textAlign: 'center', mt: 1, mb: 1 }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 5,
              }}
            >
              <ManageSearchIcon fontSize="large" color="primary" />
              <span>
                <b>Search and Filter Controls</b>
              </span>
            </div>
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <DoubleDatePicker dateFilterAction={dateFilterAction} />
          <br />
          <FilterBar
            filterQueryAction={filterQueryAction}
            currentlySelectedFilters={filters}
            filterKeys={PickupTaskFilterKeys.filter(
              (item) => item != 'merchant'
            )}
          />
        </AccordionDetails>
      </Accordion>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: 10,
          marginLeft: 10,
        }}
      ></div>
      <div style={{ width: '100%', textAlign: 'center' }}>
        <Chip
          color="primary"
          label={`${pickupsCount} Pickups`}
          variant={'outlined'}
          sx={{ padding: 1 }}
        />
      </div>
      <ResponsivePickupTasksListing
        tasksList={pickupList}
        isLoading={isLoading}
        setEditingTask={setEditingTask}
        setReceiveOrdersForTask={setReceiveOrdersForTask}
        setCancellingPickupRequest={setCancellingPickupRequest}
        setDownloadWaybillForTask={setDownloadWaybillForTask}
      />

      <TablePagination
        component="div"
        onPageChange={handlePageChange}
        page={controller.page}
        count={pickupsCount}
        rowsPerPage={controller.rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100, 200, 250, 500, 1000]}
        labelRowsPerPage=""
        style={{ overflow: 'visible' }}
      />
      {downloadWaybillForTask && (
        <TaskWaybillsDialog
          task={downloadWaybillForTask}
          onClose={() => setDownloadWaybillForTask(undefined)}
        />
      )}
      {receiveOrdersForTask && (
        <ReceiveOrdersDialog
          task={receiveOrdersForTask}
          onClose={() => setReceiveOrdersForTask(undefined)}
        />
      )}
      {editingTask && (
        <PickupTaskDialog
          taskID={editingTask.id}
          onClose={() => setEditingTask(undefined)}
        />
      )}

      <ConfirmAction
        message={'Are you sure you want to cancel this order?'}
        action={handleCancelTask}
        isOpen={cancellingPickupRequest != undefined}
        handleClose={() => setCancellingPickupRequest(undefined)}
        isSevereAction={true}
        confirmSevereActionTarget={'cancel request'}
      >
        <Alert severity="warning">This action can not be undone.</Alert>
      </ConfirmAction>
    </Box>
  );
}

export default MerchantPickups;
